import React, { useEffect, useMemo, useState } from "react";
import Prism from "prismjs";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-json";
import { Badge, Button, Card, Col, Nav, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import apiSpecDocument from "../API_SPEC.json";

const APIKEY_AUTH = [
  {
    name: "apikey",
    type: "string",
    location: "query",
    required: true,
    description: "API 키 (대부분의 엔드포인트에 필요)",
  },
];

const COOKIE_AUTH = [
  {
    name: "access_token",
    type: "cookie",
    location: "cookie",
    required: true,
    description: "Bearer JWT (로그인 후 발급)",
  },
];

const dateRangeParams = [
  { name: "ticker", type: "string", required: false, description: "티커 심볼", example: "AAPL" },
  { name: "datefrom", type: "date", required: false, description: "시작일 (없으면 1년 전)", example: "2024-01-01" },
  { name: "dateto", type: "date", required: false, description: "종료일 (없으면 오늘)", example: "2024-12-31" },
  { name: "updatefrom", type: "datetime", required: false, description: "업데이트 시작시간 (있으면 datefrom/dateto 무시)" },
  { name: "updateto", type: "datetime", required: false, description: "업데이트 종료시간" },
  { name: "page", type: "int", required: false, description: "기본값 1" },
  { name: "size", type: "int", required: false, description: "기본값 100, 최대 1000" },
];

const curlSample = `curl --request GET \\
  --url '/api/v1/companynews?apikey=YOUR_API_KEY&ticker=AAPL' \\
  --header 'accept: application/json'`;

const responseSample = `{
  "success": true,
  "message": "뉴스 데이터를 성공적으로 조회했습니다. (전체 1건 중 1건 반환, 페이지 1/1)",
  "data": [
    {
      "documentid": "0000320193-25-000123",
      "ticker": "AAPL",
      "tickerpriority": 1,
      "cik": "0000320193",
      "company": "Apple Inc",
      "pubdate": "2025-01-10",
      "reportdate": "2025-01-10T15:30:00",
      "updated": "2025-01-10T16:00:00",
      "category": "Earnings",
      "newstitle": "Apple reports Q1 results",
      "newsmain": "...",
      "goodorbad": "good",
      "importance": 5,
      "link": "https://example.com/news",
      "form": "8-K"
    }
  ],
  "total": 1,
  "page": 1,
  "size": 100,
  "total_pages": 1,
  "has_next": false,
  "has_prev": false,
  "date_from": "2024-01-10",
  "date_to": "2025-01-10",
  "update_from": "None",
  "update_to": "None"
}`;

const APIKEY_ERROR_EXAMPLES = [
  {
    title: "Response (401 Unauthorized - Missing API Key)",
    body: `{
  "detail": "API 키가 필요합니다. apikey 파라미터를 확인해주세요."
}`,
  },
  {
    title: "Response (401 Unauthorized - Invalid API Key)",
    body: `{
  "detail": "유효하지 않은 API 키입니다."
}`,
  },
];

const AUTH_REQUIRED_ERROR = {
  title: "Response (401 Unauthorized)",
  body: `{
  "detail": "Not authenticated"
}`,
};

const endpointSections = [
  {
    id: "company",
    label: "Company",
    description: "회사 기본 정보/자동완성 등 메타 정보를 조회합니다.",
    endpoints: [
      {
        id: "company-info",
        method: "GET",
        path: "/api/v1/companyinfo",
        summary: "회사 기본 정보 조회",
        authRows: APIKEY_AUTH,
        queryParams: [
          { name: "company", type: "string", required: false, description: "회사명 검색어", example: "apple" },
          { name: "ticker", type: "string", required: false, description: "티커 심볼" },
          { name: "exchange", type: "string", required: false, description: "거래소 검색어" },
          { name: "stocktype", type: "string", required: false, description: "주식 타입" },
          { name: "page", type: "int", required: false, description: "기본값 1" },
          { name: "size", type: "int", required: false, description: "기본값 100, 최대 1000" },
        ],
        responseExample: `{
  "success": true,
  "message": "회사 정보를 성공적으로 조회했습니다. (전체 1건 중 1건 반환, 페이지 1/1)",
  "data": [
    {
      "ticker": "AAPL",
      "exchange": "NASDAQ",
      "stocktype": "Common",
      "sector": "Technology",
      "companykor": "애플",
      "nickname": "Apple",
      "intro": "Consumer electronics and services."
    }
  ],
  "total": 1
}`,
      },
      {
        id: "company-autocomplete",
        method: "GET",
        path: "/api/v1/company_autocomplete",
        summary: "회사 자동완성 검색",
        authRows: APIKEY_AUTH,
        queryParams: [
          { name: "q", type: "string", required: true, description: "자동완성 검색어", example: "apple" },
          { name: "limit", type: "int", required: false, description: "기본값 20, 최대 50" },
        ],
        responseExample: `{
  "success": true,
  "message": "자동완성 결과 2건을 반환했습니다.",
  "data": [
    { "ticker": "AAPL", "companykor": "애플", "nickname": "Apple" },
    { "ticker": "APLE", "companykor": "애플 호스피탈리티", "nickname": "Apple Hosp" }
  ],
  "total": 2
}`,
      },
      {
        id: "company-by-tickerlist",
        method: "GET",
        path: "/api/v1/companyinfo_by_tickerlist",
        summary: "티커 리스트 최신 회사 정보",
        authRows: APIKEY_AUTH,
        queryParams: [
          { name: "ticker", type: "string", required: true, description: "콤마로 구분된 티커 리스트", example: "AAPL,MSFT" },
        ],
        responseExample: `{
  "success": true,
  "message": "2건의 최신 회사 정보를 반환했습니다.",
  "data": [
    {
      "ticker": "AAPL",
      "exchange": "NASDAQ",
      "stocktype": "Common",
      "sector": "Technology",
      "companykor": "애플",
      "nickname": "Apple"
    }
  ],
  "total": 2
}`,
      },
    ],
  },
  {
    id: "companynews",
    label: "Company News",
    description: "기업 공시 뉴스 조회 및 기간 필터링을 지원합니다.",
    endpoints: [
      {
        id: "company-news",
        method: "GET",
        path: "/api/v1/companynews",
        summary: "기업 공시 뉴스 조회",
        authRows: APIKEY_AUTH,
        queryParams: dateRangeParams,
        responseExample: responseSample,
      },
    ],
  },
  {
    id: "companycollect",
    label: "Company Collect Data",
    description: "공시 세부 분석 정보 조회",
    endpoints: [
      {
        id: "company-collect",
        method: "GET",
        path: "/api/v1/companycollectdata",
        summary: "공시 세부 분석 정보 조회",
        authRows: APIKEY_AUTH,
        queryParams: dateRangeParams,
        responseExample: `{
  "success": true,
  "message": "뉴스 데이터를 성공적으로 조회했습니다. (전체 1건 중 1건 반환, 페이지 1/1)",
  "data": [
    {
      "documentid": "0000320193-25-000124",
      "ticker": "AAPL",
      "company": "Apple Inc",
      "reportdate": "2025-01-10T15:30:00",
      "financial_result": "Revenue beat estimates",
      "division_performance": "iPhone growth"
    }
  ],
  "total": 1
}`,
      },
    ],
  },
  {
    id: "curation",
    label: "Curation",
    description: "큐레이션 데이터를 조회합니다.",
    endpoints: [
      {
        id: "curation-list",
        method: "GET",
        path: "/api/v1/curation",
        summary: "큐레이션 데이터 조회",
        authRows: APIKEY_AUTH,
        queryParams: [
          { name: "pubdate", type: "date", required: false, description: "발행일 (없으면 최근 날짜)", example: "2025-01-10" },
        ],
        responseExample: `{
  "success": true,
  "message": "큐레이션 데이터를 성공적으로 조회했습니다. (36건 반환)",
  "data": [
    {
      "seltype": "TOP_GAINERS",
      "ticker": "AAPL",
      "companyKor": "애플",
      "rank": 1
    }
  ],
  "total": 36
}`,
      },
    ],
  },
  {
    id: "forms",
    label: "Forms",
    description: "Form4/144 등 공시 폼 데이터를 조회합니다.",
    endpoints: [
      {
        id: "form4",
        method: "GET",
        path: "/api/v1/form4",
        summary: "Form4 조회",
        authRows: APIKEY_AUTH,
        queryParams: dateRangeParams,
        responseExample: `{
  "success": true,
  "message": "Form4 데이터를 성공적으로 조회했습니다. (전체 1건 중 1건 반환, 페이지 1/1)",
  "data": [
    {
      "documentid": "0000897423-24-000123",
      "ticker": "AAPL",
      "transactioncode": "P",
      "transactionamount": 1500000.0
    }
  ],
  "total": 1
}`,
      },
      {
        id: "form144",
        method: "GET",
        path: "/api/v1/form144",
        summary: "Form144 조회",
        authRows: APIKEY_AUTH,
        queryParams: dateRangeParams,
        responseExample: `{
  "success": true,
  "message": "Form144 데이터를 성공적으로 조회했습니다. (전체 1건 중 1건 반환, 페이지 1/1)",
  "data": [
    {
      "documentid": "0000897423-24-000456",
      "ticker": "AAPL",
      "marketval": 1500000.0,
      "approximatedate": "2025-02-01"
    }
  ],
  "total": 1
}`,
      },
      {
        id: "form4-ticker",
        method: "GET",
        path: "/api/v1/form4/ticker",
        summary: "티커 + 리포터 기준 Form4 조회",
        authRows: APIKEY_AUTH,
        queryParams: [
          { name: "ticker", type: "string", required: true, description: "티커 심볼", example: "AAPL" },
          { name: "reporter", type: "string", required: true, description: "리포터 이름", example: "Tim Cook" },
        ],
        responseExample: `[
  {
    "documentid": "0000897423-24-000123",
    "ticker": "AAPL",
    "reportername": "Tim Cook",
    "transactionamount": 1500000.0
  }
]`,
      },
    ],
  },
  /* {
    id: "kor-fs-extract",
    label: "Korean FS Extract",
    description: "한국 기업 실적 데이터 조회",
    endpoints: [
      {
        id: "korfsextract",
        method: "GET",
        path: "/api/v1/korfsextract",
        summary: "한국 기업 실적 데이터 조회",
        authRows: APIKEY_AUTH,
        queryParams: [
          { name: "code", type: "string", required: false, description: "한국주식코드", example: "005930" },
          { name: "corpcode", type: "string", required: false, description: "기업코드" },
          { name: "page", type: "int", required: false, description: "기본값 1" },
          { name: "size", type: "int", required: false, description: "기본값 100, 최대 1000" },
        ],
        responseExample: `{
  "success": true,
  "message": "korFsExtract 데이터를 성공적으로 조회했습니다. (전체 1건 중 1건 반환, 페이지 1/1)",
  "data": [
    {
      "code": "005930",
      "corpcode": "00126380",
      "company": "삼성전자",
      "tgdate": "2024-12-31"
    }
  ],
  "total": 1
}`,
      },
    ],
  }, */
  {
    id: "company-fs-extract",
    label: "Company FS Extract",
    description: "미국 기업 실적 데이터 조회",
    endpoints: [
      {
        id: "company-fs",
        method: "GET",
        path: "/api/v1/company-fs-extract",
        summary: "미국 기업 실적 데이터 조회",
        authRows: APIKEY_AUTH,
        queryParams: [
          { name: "ticker", type: "string", required: true, description: "티커 심볼", example: "AAPL" },
        ],
        responseExample: `{
  "success": true,
  "items": [
    {
      "ticker": "AAPL",
      "company": "Apple Inc",
      "tgdate": "2024-12-31",
      "revenue": 119575000000
    }
  ],
  "count": 1
}`,
      },
    ],
  },
  {
    id: "guru",
    label: "Guru",
    description: "투자대가/연기금 관련 데이터",
    endpoints: [
      {
        id: "guru-list",
        method: "GET",
        path: "/api/v1/guru",
        summary: "투자대가/연기금 리스트 조회",
        authRows: [],
        queryParams: [
          { name: "category", type: "string", required: true, description: "GURU 또는 FUND", example: "GURU" },
        ],
        responseExample: `[
  {
    "guruid": 1,
    "category": "GURU",
    "leader": "Warren Buffett",
    "nickname": "Buffett"
  }
]`,
      },
      {
        id: "guru-history",
        method: "GET",
        path: "/api/v1/guru_history_by_ticker",
        summary: "티커별 guru 히스토리",
        authRows: [],
        queryParams: [
          { name: "ticker", type: "string", required: true, description: "티커 심볼", example: "AAPL" },
        ],
        responseExample: `{
  "items": [
    {
      "guruid": 1,
      "category": "GURU",
      "rank": 1,
      "nickname": "Buffett"
    }
  ],
  "total": 1
}`,
      },
      {
        id: "guru-portfolio",
        method: "GET",
        path: "/api/v1/guru_portfolio",
        summary: "guru 포트폴리오 상세 조회",
        authRows: [],
        queryParams: [
          { name: "guruid", type: "int", required: true, description: "Guru ID", example: 1 },
        ],
        responseExample: `{
  "aboutPortfolio": {
    "guruid": 1,
    "leader": "Warren Buffett",
    "nickname": "Buffett"
  },
  "allTickers": [
    {
      "ticker": "AAPL",
      "percent": 5.1
    }
  ]
}`,
      },
    ],
  },
  {
    id: "users",
    label: "Users",
    description: "로그인 사용자 정보 및 로그아웃",
    endpoints: [
      {
        id: "me",
        method: "GET",
        path: "/api/v1/me",
        summary: "현재 로그인한 사용자 정보",
        authRows: COOKIE_AUTH,
        queryParams: [],
        responseExample: `{
  "user_id": "3f3b1c90-2d0f-4c9d-9f1c-2d5b2a2d9f91",
  "authenticated": true
}`,
      },
      {
        id: "logout",
        method: "POST",
        path: "/api/v1/logout",
        summary: "로그아웃 처리",
        authRows: COOKIE_AUTH,
        queryParams: [],
        responseExample: `{
  "user_id": "3f3b1c90-2d0f-4c9d-9f1c-2d5b2a2d9f91",
  "authenticated": false
}`,
      },
    ],
  },
];

const methodBadgeMap = {
  GET: { bg: "success-subtle", text: "success" },
  POST: { bg: "warning-subtle", text: "warning" },
};

const formatRequired = (value) => {
  if (value === true) return "required";
  if (value === false) return "optional";
  return value || "-";
};

const renderTable = (columns, rows) => {
  const renderCell = (column, row) => {
    if (column.key === "name") {
      return <span className="api-param-name">{row[column.key] || "-"}</span>;
    }
    if (column.key === "type") {
      return (
        <span className="badge api-type-badge">
          {row[column.key] || "-"}
        </span>
      );
    }
    if (column.key === "required") {
      const isRequired = row[column.key] === true;
      const isOptional = row[column.key] === false;
      const label = isRequired ? "Required" : isOptional ? "Optional" : formatRequired(row[column.key]);
      if (!isRequired && !isOptional) {
        return label;
      }
      return (
        <span
          className={`badge ${isRequired ? "api-required-badge" : "api-optional-badge"}`}
        >
          {label}
        </span>
      );
    }
    return row[column.key] || "-";
  };

  return (
    <Table responsive className="mb-0 api-table">
      <thead>
        <tr className="text-secondary fs-sm">
          {columns.map((column) => (
            <th key={column.key}>{column.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.name || row.key}>
            {columns.map((column) => (
              <td key={column.key}>{renderCell(column, row)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const renderParamBlock = (title, rows, columns, emptyMessage = "파라미터 없음") => (
  <div className="mb-4">
    <div className="api-section-title mb-2">{title}</div>
    {rows && rows.length > 0 ? (
      renderTable(columns, rows)
    ) : (
      <div className="text-secondary fs-sm">{emptyMessage}</div>
    )}
  </div>
);

const MethodBadge = ({ method }) => {
  const style = methodBadgeMap[method] || { bg: "secondary", text: "light" };
  return (
    <Badge bg={style.bg} text={style.text} className="px-2 py-1">
      {method}
    </Badge>
  );
};

const resolveEndpointId = (endpointId, fallbackId) => {
  if (!endpointId) return fallbackId;
  if (endpointId === "companynews") return "company-news";
  return endpointId;
};

const buildCurlSample = (endpoint) => {
  if (!endpoint) return curlSample;
  if (endpoint.curlSample) return endpoint.curlSample;
  const method = endpoint.method || "GET";
  const hasApiKey = endpoint.authRows?.some((row) => row.name === "apikey");
  const hasCookie = endpoint.authRows?.some((row) => row.location === "cookie");
  let url = endpoint.path;
  if (endpoint.pathParams?.length) {
    endpoint.pathParams.forEach((param) => {
      const value = param.example || `<${param.name}>`;
      url = url.replace(`{${param.name}}`, value);
    });
  }
  const queryParts = [];
  if (hasApiKey) {
    queryParts.push("apikey=YOUR_API_KEY");
  }
  if (endpoint.queryParams?.length) {
    endpoint.queryParams.forEach((param) => {
      if (!param.required && !param.example) return;
      const value = param.example ?? `<${param.name}>`;
      queryParts.push(`${param.name}=${value}`);
    });
  }
  if (queryParts.length > 0) {
    url = `${url}?${queryParts.join("&")}`;
  }
  const lines = [
    `curl --request ${method} \\\\`,
    `  --url '${url}' \\\\`,
    `  --header 'accept: application/json'`,
  ];
  if (endpoint.bodyParams?.length) {
    const body = endpoint.bodyParams.reduce((acc, row) => {
      acc[row.name] = row.example || "";
      return acc;
    }, {});
    lines.push(`  --header 'content-type: application/json' \\\\`);
    lines.push(`  --data '${JSON.stringify(body, null, 2)}'`);
  }
  if (hasCookie) {
    lines.push(`  --header 'Cookie: access_token=Bearer <JWT>'`);
  }
  return lines.join("\n");
};

const buildErrorExamples = (endpoint) => {
  if (!endpoint) return [];
  if (endpoint.errorExamples) return endpoint.errorExamples;
  const hasApiKey = endpoint.authRows?.some((row) => row.name === "apikey");
  const hasCookie = endpoint.authRows?.some((row) => row.location === "cookie");
  if (hasApiKey) return APIKEY_ERROR_EXAMPLES;
  if (hasCookie) return [AUTH_REQUIRED_ERROR];
  return [];
};

const stringifyJson = (value) => {
  if (value === null || value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return "";
  }
};

const getOpenApiResponseExample = (path, method = "GET") => {
  const operation = apiSpecDocument?.paths?.[path]?.[method.toLowerCase()];
  const content = operation?.responses?.["200"]?.content?.["application/json"];
  if (!content) return "";

  if (Object.prototype.hasOwnProperty.call(content, "example")) {
    return stringifyJson(content.example);
  }

  if (content.examples && typeof content.examples === "object") {
    const firstExample = Object.values(content.examples)[0];
    if (firstExample && typeof firstExample === "object" && Object.prototype.hasOwnProperty.call(firstExample, "value")) {
      return stringifyJson(firstExample.value);
    }
    return stringifyJson(firstExample);
  }

  return "";
};

export default function ApiSpec() {
  const { endpointId } = useParams();
  const navigate = useNavigate();
  const [responseTab, setResponseTab] = useState("success");

  const endpointMap = useMemo(() => {
    const map = {};
    endpointSections.forEach((section) => {
      section.endpoints.forEach((endpoint) => {
        map[endpoint.id] = {
          ...endpoint,
          sectionId: section.id,
          sectionLabel: section.label,
          sectionDescription: section.description,
        };
      });
    });
    return map;
  }, []);

  const responseExamplesFromSpec = useMemo(() => {
    const map = {};
    endpointSections.forEach((section) => {
      section.endpoints.forEach((endpoint) => {
        const example = getOpenApiResponseExample(endpoint.path, endpoint.method || "GET");
        if (example) {
          map[endpoint.id] = example;
        }
      });
    });
    return map;
  }, []);

  const defaultEndpointId = endpointSections[0]?.endpoints?.[0]?.id;
  const activeEndpointId = resolveEndpointId(endpointId, defaultEndpointId);
  const activeEndpoint = endpointMap[activeEndpointId] || endpointMap[defaultEndpointId];
  const activeResponseExample =
    (activeEndpoint && responseExamplesFromSpec[activeEndpoint.id]) ||
    activeEndpoint?.responseExample ||
    "";
  const activeSection = activeEndpoint
    ? endpointSections.find((section) => section.id === activeEndpoint.sectionId)
    : null;
  const endpointNote = activeEndpoint?.note
    || (activeEndpoint?.summary ? `${activeEndpoint.summary} 데이터를 제공합니다.` : "");
  const errorExamples = buildErrorExamples(activeEndpoint);
  const hasSuccessResponse = Boolean(activeResponseExample);
  const hasErrorResponse = errorExamples.length > 0;

  useEffect(() => {
    if (hasSuccessResponse) {
      setResponseTab("success");
      return;
    }
    if (hasErrorResponse) {
      setResponseTab("error");
    }
  }, [activeEndpointId, hasSuccessResponse, hasErrorResponse]);

  useEffect(() => {
    Prism.highlightAll();
  }, [activeEndpointId, responseTab]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeEndpointId]);

  const handleCopy = (text) => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handleResponseTab = (event, tabKey) => {
    event.preventDefault();
    if (responseTab !== tabKey) {
      setResponseTab(tabKey);
    }
  };

  const responseCopyText = useMemo(() => {
    if (responseTab === "success" && hasSuccessResponse) {
      return activeResponseExample;
    }
    if (responseTab === "error" && hasErrorResponse) {
      return errorExamples
        .map((example) => `${example.title}\n${example.body}`)
        .join("\n\n");
    }
    return "";
  }, [responseTab, hasSuccessResponse, hasErrorResponse, activeResponseExample, errorExamples]);

  const handleMobileEndpointChange = (event) => {
    const nextEndpointId = event.target.value;
    if (!nextEndpointId) return;
    navigate(`/docs/api/${nextEndpointId}`);
  };

  return (
    <React.Fragment>
      <div className="p-3 p-lg-4 api-spec">
        <Row className="g-4">
          <Col xl={3} className="d-none d-xl-block">
            <div className="position-sticky" style={{ top: "90px" }}>
              <Card>
                <Card.Body>
                  <div className="api-sidebar-title text-uppercase fw-semibold fs-xs mb-3">API Reference</div>
                  <Nav className="nav-sidebar">
                    {endpointSections.map((section) => (
                      <div className="nav-item show" key={section.id}>
                        <span className="api-nav-title">{section.label}</span>
                        <div className="nav-sub">
                          {section.endpoints.map((endpoint) => (
                            <Link
                              key={endpoint.id}
                              className={`nav-sub-link${activeEndpointId === endpoint.id ? " active" : ""}`}
                              to={`/docs/api/${endpoint.id}`}
                            >
                              {endpoint.method} {endpoint.path}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </Nav>
                </Card.Body>
              </Card>
            </div>
          </Col>

          <Col xl={9}>
            <div className="d-xl-none mb-3">
              <Card className="api-mobile-nav">
                <Card.Body>
                  <div className="api-sidebar-title text-uppercase fw-semibold fs-xs mb-2">API Reference</div>
                  <div className="api-mobile-select-wrap">
                    <label htmlFor="api-endpoint-select" className="api-mobile-select-label">Endpoint</label>
                    <select
                      id="api-endpoint-select"
                      className="form-select form-select-sm api-mobile-select"
                      value={activeEndpointId || defaultEndpointId || ""}
                      onChange={handleMobileEndpointChange}
                    >
                      {endpointSections.map((section) => (
                        <optgroup label={section.label} key={section.id}>
                          {section.endpoints.map((endpoint) => (
                            <option value={endpoint.id} key={endpoint.id}>
                              {endpoint.method} {endpoint.path}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </Card.Body>
              </Card>
            </div>

            <div className="api-spec-header mb-4">
              <div className="api-spec-eyebrow">{activeSection?.label || "API Reference"}</div>
              <h1 className="api-spec-title">
                {activeEndpoint?.title || activeEndpoint?.summary || activeSection?.label || "API Reference"}
              </h1>
              <p className="api-spec-subtitle">
                {activeEndpoint?.description ||
                  activeSection?.description ||
                  "Base URL: /api/v1. 각 엔드포인트별 인증/파라미터/응답 예시를 확인할 수 있습니다."}
              </p>
            </div>

            <div className="api-endpoint-bar mb-3">
              <MethodBadge method={activeEndpoint?.method || "GET"} />
              <code className="api-endpoint-path">{activeEndpoint?.path || "/api/v1"}</code>
              <Button variant="success" size="sm">Try it</Button>
            </div>

            {endpointNote && (
              <Card className="api-note mb-4">
                <Card.Body className="d-flex align-items-start gap-2">
                  <i className="ri-information-line"></i>
                  <span>{endpointNote}</span>
                </Card.Body>
              </Card>
            )}

            {activeEndpoint ? (
              <>
                {renderParamBlock(
                  "Authorizations",
                  activeEndpoint.authRows,
                  [
                    { key: "name", label: "Name" },
                    { key: "type", label: "Type" },
                    { key: "location", label: "Location" },
                    { key: "required", label: "Required" },
                    { key: "description", label: "Description" },
                  ],
                  "인증 필요 없음"
                )}

                {renderParamBlock(
                  "Path Parameters",
                  activeEndpoint.pathParams,
                  [
                    { key: "name", label: "Name" },
                    { key: "type", label: "Type" },
                    { key: "required", label: "Required" },
                    { key: "description", label: "Description" },
                  ]
                )}

                {renderParamBlock(
                  "Query Parameters",
                  activeEndpoint.queryParams,
                  [
                    { key: "name", label: "Name" },
                    { key: "type", label: "Type" },
                    { key: "required", label: "Required" },
                    { key: "description", label: "Description" },
                  ]
                )}

                {renderParamBlock(
                  "Body Parameters",
                  activeEndpoint.bodyParams,
                  [
                    { key: "name", label: "Name" },
                    { key: "type", label: "Type" },
                    { key: "required", label: "Required" },
                    { key: "description", label: "Description" },
                  ]
                )}

                <div className="mb-4">
                  <div className="api-section-title mb-2">Code Example</div>
                  <Card className="api-code-card">
                    <Card.Header className="d-flex align-items-center justify-content-between">
                      <span className="fw-semibold">cURL</span>
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={() => handleCopy(buildCurlSample(activeEndpoint))}
                      >
                        Copy
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      <pre className="mb-0"><code className="language-bash">{buildCurlSample(activeEndpoint)}</code></pre>
                    </Card.Body>
                  </Card>
                </div>

                {(hasSuccessResponse || hasErrorResponse) && (
                  <div className="mb-4">
                    <div className="api-section-title mb-2">Response</div>
                    <Card className="api-code-card">
                      <Card.Header className="d-flex align-items-center justify-content-between">
                        <Nav className="nav-line api-response-tabs">
                          {hasSuccessResponse && (
                            <Nav.Link
                              href="#"
                              active={responseTab === "success"}
                              onClick={(event) => handleResponseTab(event, "success")}
                            >
                              Response (200 OK)
                            </Nav.Link>
                          )}
                          {hasErrorResponse && (
                            <Nav.Link
                              href="#"
                              active={responseTab === "error"}
                              onClick={(event) => handleResponseTab(event, "error")}
                            >
                              Error Responses
                            </Nav.Link>
                          )}
                        </Nav>
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() => handleCopy(responseCopyText)}
                        >
                          Copy
                        </Button>
                      </Card.Header>
                      <Card.Body>
                        {responseTab === "success" && hasSuccessResponse && (
                          <pre className="mb-0"><code className="language-json">{activeResponseExample}</code></pre>
                        )}
                        {responseTab === "error" && hasErrorResponse && (
                          <div className="api-error-stack">
                            {errorExamples.map((errorExample) => (
                              <div className="api-error-block" key={errorExample.title}>
                                <div className="api-error-title">{errorExample.title}</div>
                                <pre className="mb-0"><code className="language-json">{errorExample.body}</code></pre>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </>
            ) : (
              <div className="text-secondary">선택한 엔드포인트를 찾을 수 없습니다.</div>
            )}
          </Col>

        </Row>
      </div>
    </React.Fragment>
  );
}
